import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  LinearProgress,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";


interface UploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (file: File, name: string) => void;
  type: "animation" | "image";
}

const UploadDialog: React.FC<UploadDialogProps> = ({
  open,
  onClose,
  onSubmit,
  type,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [touched, setTouched] = useState(false);
  const [progress, _setProgress] = useState(0); // Track upload progress
  const { t } = useTranslation();


  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
      // setFileName(file.name);
      setPreviewUrl(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: type === "animation" ? { "video/*": [] } : { "image/*": [] },
    maxFiles: 1,
  });

  const handleSubmit = () => {
    if (selectedFile && fileName) {
      onSubmit(selectedFile, fileName);

      handleClose();
    }
  };


  const handleClose = () => {
    setSelectedFile(null);
    setFileName("");
    setTouched(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          maxWidth: 600,
        },
      }}
    >

      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6">
          {t("upload")} {t(type === "animation" ? "animation" : "image")}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: "#666" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 1, m: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder={`${t(type === "animation" ? "animation" : "image")} ${t("title")}`}
              value={fileName}
              onChange={(e) => {
                setFileName(e.target.value);
                setTouched(true);
              }}
              variant="outlined"
              error={touched && !fileName}
              sx={{ mb: 1 }}
            />
            {touched && !fileName && (
              <Typography
                color="error"
                variant="caption"
                sx={{ display: "block", mb: 2 }}
              >
                {t(type === "animation" ? "animation" : "image")}
                {t("title_is_required")}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Box
              {...getRootProps()}
              sx={{
                border: "1px dashed #ccc",
                borderRadius: 1,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: isDragActive ? "#f5f5f5" : "transparent",
              }}
            >
              <input {...getInputProps()} />
              <Typography>
                {isDragActive
                  ? t("drop_files_here")
                  : t("drop_files_here_or_click_to_upload")}
              </Typography>
            </Box>
          </Grid>

          {previewUrl && (
            <Grid item xs={12}>
              <Box sx={{ mt: 2, textAlign: "center" }}>
                {type === "animation" ? (
                  <video
                    src={previewUrl}
                    controls
                    style={{ maxWidth: "100%", maxHeight: "300px" }}
                  />
                ) : (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{ maxWidth: "100%", maxHeight: "300px" }}
                  />
                )}
              </Box>
            </Grid>
          )}
        </Grid>
        {progress > 0 && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ flex: 1 }}
              />
              <Typography variant="body2">{`${progress}%`}</Typography>
            </Box>
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: "1px solid #e0e0e0",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            color: "#666",
            textTransform: "none",
            borderColor: "#ccc",
            "&:hover": {
              borderColor: "#999",
            },
          }}
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!selectedFile || !fileName}
          className="addbtn"
          variant="contained"
          sx={{
            color: "white",
            bgcolor: "#1976d2",
            "&:hover": {
              bgcolor: "#c6d0da",
            },
            "&.Mui-disabled": {
              color: "#ffff1",
              bgcolor: "#c6d0da",
            },
          }}
        >
          {t("submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDialog;
